import * as React from "react";
import { RefreshCw } from "./icons";

import { Button } from "./Button";
import { ErrorState } from "./EmptyState";

interface ErrorBoundaryProps {
  children: React.ReactNode;
  /** Changing this value clears the error, e.g. the current pathname. */
  resetKey?: unknown;
  /** Custom fallback; defaults to an ErrorState with a reload button. */
  fallback?: React.ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/** Keeps a crash inside one page from blanking the whole app. */
export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("Page crashed", error, info.componentStack);
  }

  componentDidUpdate(prev: ErrorBoundaryProps) {
    if (this.state.error && prev.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  render() {
    if (!this.state.error) {
      return this.props.children;
    }
    return (
      this.props.fallback ?? (
        <div
          data-ui="error-boundary"
          className="flex min-h-[60vh] items-center justify-center px-6"
        >
          <ErrorState
            title="This page hit a problem"
            description="Something went wrong while showing this page. Reload to try again; the rest of SAP still works."
            action={
              <Button
                variant="secondary"
                leftIcon={<RefreshCw />}
                onClick={() => window.location.reload()}
              >
                Reload page
              </Button>
            }
          />
        </div>
      )
    );
  }
}
